<template>
  <!-- The copied board link the way a teammate sees it: a Slack message with
  a live board unfurl. Context: boardName, adThumbnails, adCount -->
  <div>
    <!-- Channel header -->
    <div class="flex items-center gap-2 px-3.5 py-2 border-b border-border-normal">
      <SlackIcon
        :width="14"
        :height="14"
      />
      <BaseText
        type="label"
        size="xs"
        class="text-text-muted"
      >
        # creative
      </BaseText>
      <div class="ml-auto flex items-center gap-1">
        <span class="scene-dot" />
        <span class="scene-dot" />
        <span class="scene-dot" />
      </div>
    </div>
    <!-- Message -->
    <div class="flex items-start gap-2.5 px-3.5 pt-3 pb-3.5">
      <div class="flex-shrink-0 w-8 h-8 rounded-md overflow-hidden flex items-center justify-center bg-primary-blue-100">
        <img
          v-if="avatarUrl"
          :src="avatarUrl"
          alt=""
          class="w-full h-full object-cover"
        >
        <BaseText
          v-else
          type="label"
          size="xs"
          class="text-white"
        >
          {{ initials }}
        </BaseText>
      </div>
      <div class="min-w-0 flex-grow">
        <div class="flex items-baseline gap-1.5">
          <BaseText
            type="label"
            size="sm"
            class="text-text-loud truncate"
          >
            {{ senderName }}
          </BaseText>
          <BaseText
            type="body"
            size="xs"
            class="text-text-subdued flex-shrink-0"
          >
            just now
          </BaseText>
        </div>
        <BaseText
          type="body"
          size="sm"
          class="text-text-muted mt-0.5"
        >
          Pulled the references for the next round into a board — it updates as we add to it.
        </BaseText>
        <!-- Link unfurl -->
        <div class="scene-unfurl mt-2 flex gap-3 pl-3 py-2 pr-2.5 rounded-md">
          <div class="min-w-0 flex-grow">
            <div class="flex items-center gap-1.5">
              <div class="w-4 h-4 rounded flex items-center justify-center bg-neutral-900 flex-shrink-0">
                <ForeplayIcon
                  :width="12"
                  :height="12"
                />
              </div>
              <BaseText
                type="label"
                size="xs"
                class="text-text-subdued"
              >
                Foreplay
              </BaseText>
            </div>
            <BaseText
              type="label"
              size="sm"
              class="text-primary-blue-100 mt-1 truncate"
            >
              {{ boardLabel }}
            </BaseText>
            <BaseText
              type="body"
              size="xs"
              class="text-text-subdued mt-0.5"
            >
              {{ adCountLabel }} · updates live · no login needed
            </BaseText>
          </div>
          <!-- Stacked thumbnails -->
          <div
            v-if="thumbnails.length"
            class="scene-stack relative flex-shrink-0"
          >
            <div
              v-for="(thumb, index) in thumbnails"
              :key="`thumb-${index}`"
              class="scene-thumb absolute rounded-md overflow-hidden bg-neutral-50 border border-white"
              :style="thumbStyle(index)"
            >
              <img
                :src="thumb"
                alt=""
                class="w-full h-full object-cover"
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SlackIcon from '../../globals/Icons/SlackIcon.vue'
import ForeplayIcon from '../../globals/Icons/ForeplayIcon.vue'

const MAX_THUMBNAILS = 3

export default {
  name: 'BoardSharedScene',
  components: {
    SlackIcon,
    ForeplayIcon
  },
  props: {
    context: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUserName', 'getUserAvatar']),
    senderName () {
      return this.getUserName || 'You'
    },
    avatarUrl () {
      return this.getUserAvatar || null
    },
    initials () {
      const parts = (this.getUserName || '').split(' ').filter(Boolean)
      if (!parts.length) return 'Y'
      return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join('')
    },
    boardLabel () {
      const name = this.context.boardName
      if (!name) return 'Your board'
      return name.startsWith('#') ? name : `#${name}`
    },
    thumbnails () {
      return (this.context.adThumbnails || []).filter(Boolean).slice(0, MAX_THUMBNAILS)
    },
    adCountLabel () {
      const count = this.context.adCount || this.thumbnails.length
      if (!count) return 'Live board'
      return `${count} ${count === 1 ? 'ad' : 'ads'}`
    }
  },
  methods: {
    // Fan the thumbnails out to the right so all of them peek through
    thumbStyle (index) {
      const total = this.thumbnails.length
      return {
        left: `${(total - 1 - index) * 14}px`,
        zIndex: total - index,
        transform: `rotate(${(index - (total - 1) / 2) * 5}deg)`
      }
    }
  }
}
</script>

<style scoped>
.scene-dot {
  width: 5px;
  height: 5px;
  border-radius: 9999px;
  background-color: theme('colors.neutral.100');
}
.scene-unfurl {
  border-left: 3px solid theme('colors.neutral.100');
  background-color: theme('colors.neutral.25');
}
.scene-stack {
  width: 76px;
  height: 52px;
}
.scene-thumb {
  top: 0;
  width: 44px;
  height: 52px;
  box-shadow: 0 2px 6px rgba(6, 7, 16, 0.12);
}
</style>
